// Merchant self-service business profile. Approved (and applied) merchants
// edit the details captured at signup — business/legal name, authorized
// representative, address, phone, city, exact location pin, and payout
// method/details. Feeds the certificate + nearest-merchant routing.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Store, MapPin, Home, Phone, User as UserIcon, LocateFixed, Loader2, Banknote, Settings, BadgeCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, SectionHeader, StatusPill } from '../../components/shared/primitives';
import { MapView } from '../../components/shared/MapView';
import { Button } from '../../components/stripe-ui/Button';
import { MERCHANT_NAV } from '../_navs';
import { catalog, updateMe, type UpdateMeInput, type MerchantProfileShape, type PayoutMethod } from '../../lib/api';

const PAYOUT_METHODS: Array<{ value: PayoutMethod; label: string }> = [
  { value: 'moncash',     label: 'MonCash' },
  { value: 'natcash',     label: 'NatCash' },
  { value: 'manual_bank', label: 'Bank transfer' },
  { value: 'cash',        label: 'Cash pickup' },
  { value: 'other',       label: 'Other' },
];

export function MerchantProfilePage() {
  const { user, refresh } = useAuth();
  const mp = (user?.merchant_profile ?? null) as MerchantProfileShape | null;
  const citiesQ = useQuery({ queryKey: ['catalog', 'cities'], queryFn: catalog.cities });

  const [businessName, setBusinessName] = useState(mp?.business_name ?? '');
  const [legalName, setLegalName] = useState(mp?.legal_name ?? '');
  const [rep, setRep] = useState(mp?.representative_name ?? '');
  const [address, setAddress] = useState(mp?.address_line ?? '');
  const [phone, setPhone] = useState(mp?.phone ?? '');
  const [cityId, setCityId] = useState<number | ''>(mp?.city_id ?? '');
  const [lat, setLat] = useState<number | ''>(mp?.latitude != null ? Number(mp.latitude) : '');
  const [lng, setLng] = useState<number | ''>(mp?.longitude != null ? Number(mp.longitude) : '');
  const [locating, setLocating] = useState(false);
  const [payoutMethod, setPayoutMethod] = useState<PayoutMethod | ''>(mp?.payout_method ?? '');
  const pd = mp?.payout_details ?? {};
  const [bankName, setBankName] = useState(pd.bank_name ?? '');
  const [acctName, setAcctName] = useState(pd.account_name ?? '');
  const [acctNumber, setAcctNumber] = useState(pd.account_number ?? '');
  const [payoutPhone, setPayoutPhone] = useState(pd.phone ?? '');

  const captureLocation = () => {
    if (!('geolocation' in navigator)) { toast.error('Geolocation not supported.'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setLat(Number(p.coords.latitude.toFixed(7))); setLng(Number(p.coords.longitude.toFixed(7))); setLocating(false); toast.success('Location updated'); },
      () => { setLocating(false); toast.error('Could not get location — enter it manually.'); },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const isBank = payoutMethod === 'manual_bank';
  const isWallet = payoutMethod === 'moncash' || payoutMethod === 'natcash';

  const payoutDetails = useMemo(() => {
    if (isBank) return { bank_name: bankName, account_name: acctName, account_number: acctNumber };
    if (isWallet) return { phone: payoutPhone };
    return {};
  }, [isBank, isWallet, bankName, acctName, acctNumber, payoutPhone]);

  const save = useMutation({
    mutationFn: () => {
      const input: UpdateMeInput = {
        business_name: businessName.trim(),
        legal_name: legalName.trim() || undefined,
        representative_name: rep.trim() || undefined,
        address_line: address.trim() || undefined,
        phone: phone.trim() || undefined,
        city_id: cityId === '' ? undefined : Number(cityId),
        latitude: lat === '' ? null : Number(lat),
        longitude: lng === '' ? null : Number(lng),
        payout_method: payoutMethod === '' ? undefined : payoutMethod,
        payout_details: payoutDetails,
      };
      return updateMe(input);
    },
    onSuccess: async () => { await refresh(); toast.success('Business profile saved'); },
    onError: (e: Error) => toast.error(e.message || 'Save failed'),
  });

  return (
    <PortalShell
      nav={MERCHANT_NAV}
      activeId="profile"
      title="Business profile"
      subtitle="Your storefront details — used on your certificate and to route nearby recipients to you."
      actions={<Button onClick={() => save.mutate()} loading={save.isPending} disabled={save.isPending}>Save changes</Button>}
    >
      <div className="space-y-5 max-w-3xl">
        {/* Status + ID */}
        <Card className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="inline-flex w-10 h-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600"><Store className="w-5 h-5" /></span>
            <div>
              <p className="text-[13.5px] font-semibold text-slate-900">{mp?.business_name ?? 'Merchant'}</p>
              <p className="text-[12px] text-slate-500">Merchant ID <span className="font-mono">{mp?.merchant_code ?? '—'}</span></p>
            </div>
          </div>
          <div className="flex items-center gap-1.5"><BadgeCheck className="w-4 h-4 text-slate-400" /><StatusPill status={mp?.status ?? 'applied'} /></div>
        </Card>

        {/* Business */}
        <Card className="space-y-3">
          <SectionHeader title="Business" />
          <div className="grid sm:grid-cols-2 gap-3">
            <Field icon={Store} label="Business name" value={businessName} onChange={setBusinessName} />
            <Field icon={Store} label="Legal name" value={legalName} onChange={setLegalName} />
            <Field icon={UserIcon} label="Authorized representative" value={rep} onChange={setRep} />
            <Field icon={Phone} label="Business phone" value={phone} onChange={setPhone} />
            <div className="sm:col-span-2"><Field icon={Home} label="Business address" value={address} onChange={setAddress} /></div>
            <Labeled icon={MapPin} label="City">
              <select value={cityId === '' ? '' : String(cityId)} onChange={(e) => setCityId(e.target.value === '' ? '' : Number(e.target.value))} className={selectCls}>
                <option value="">Select a city…</option>
                {(citiesQ.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Labeled>
          </div>
        </Card>

        {/* Location */}
        <Card className="space-y-3">
          <SectionHeader title="Store location" subtitle="Pin your exact location so nearby recipients are routed to you first." />
          <div className="flex flex-wrap items-end gap-3">
            <Labeled label="Latitude"><input type="number" step="any" value={lat} onChange={(e) => setLat(e.target.value === '' ? '' : Number(e.target.value))} className={inputCls + ' w-36'} /></Labeled>
            <Labeled label="Longitude"><input type="number" step="any" value={lng} onChange={(e) => setLng(e.target.value === '' ? '' : Number(e.target.value))} className={inputCls + ' w-36'} /></Labeled>
            <button type="button" onClick={captureLocation} disabled={locating} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-900 text-white text-[12.5px] font-semibold hover:bg-slate-800 disabled:opacity-60">
              {locating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LocateFixed className="w-3.5 h-3.5" />} Use current location
            </button>
          </div>
          <p className="text-[11px] text-slate-400">Drag the pin or tap the map to set your exact store location.</p>
          <MapView
            height="300px"
            zoom={lat === '' ? 8 : 15}
            draggable={{
              lat: lat === '' ? 18.9712 : Number(lat),
              lng: lng === '' ? -72.2852 : Number(lng),
              onChange: (la, lo) => { setLat(la); setLng(lo); },
            }}
          />
        </Card>

        {/* Payout */}
        <Card className="space-y-3">
          <SectionHeader title="Payout method" subtitle="How VoyeLakay pays you out (in HTG) after a delivery is confirmed." />
          <Labeled icon={Banknote} label="Method">
            <select value={payoutMethod} onChange={(e) => setPayoutMethod(e.target.value as PayoutMethod | '')} className={selectCls + ' max-w-xs'}>
              <option value="">Select…</option>
              {PAYOUT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </Labeled>
          {isBank && (
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Bank name" value={bankName} onChange={setBankName} />
              <Field label="Account name" value={acctName} onChange={setAcctName} />
              <Field label="Account number" value={acctNumber} onChange={setAcctNumber} />
            </div>
          )}
          {isWallet && (
            <div className="max-w-xs"><Field icon={Phone} label={`${payoutMethod === 'moncash' ? 'MonCash' : 'NatCash'} phone`} value={payoutPhone} onChange={setPayoutPhone} /></div>
          )}
          <p className="text-[11px] text-slate-400">Payout details are encrypted at rest.</p>
        </Card>

        <p className="text-[12px] text-slate-500">
          Looking for password, 2FA, or notification settings? <Link to="/merchant/settings" className="text-brand-600 font-semibold hover:underline inline-flex items-center gap-1"><Settings className="w-3.5 h-3.5" /> Account settings</Link>
        </p>
      </div>
    </PortalShell>
  );
}

const inputCls = 'w-full px-3 py-2 text-[13px] bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400';
const selectCls = inputCls;

function Labeled({ icon: Icon, label, children }: { icon?: React.ComponentType<{ className?: string }>; label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 inline-flex items-center gap-1">{Icon && <Icon className="w-3 h-3" />} {label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Field({ icon, label, value, onChange }: { icon?: React.ComponentType<{ className?: string }>; label: string; value: string; onChange: (v: string) => void }) {
  return <Labeled icon={icon} label={label}><input value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} /></Labeled>;
}
